import React from 'react';

import { useDataContext } from '../context/DataContext.tsx';
import { DataContext } from '../context/DataContext.tsx';
import { useSurfaceContext } from '../context/SurfaceContext.tsx';
import type { ChildList, ChildReference } from '../types/A2UICommon.types.tsx';
import { isChildTemplate } from '../types/A2UICommon.types.tsx';
import { resolvePointerPath } from '../utils/jsonPointer.ts';

// Forward declaration — TreeBuilder will register itself to avoid circular imports
let ComponentRendererRef: React.ComponentType<{
	componentId: string;
	visited?: Set<string>;
}> | null = null;

export function registerComponentRenderer(
	renderer: React.ComponentType<{ componentId: string; visited?: Set<string> }>,
): void {
	ComponentRendererRef = renderer;
}

function encodeJsonPointerToken(token: string): string {
	return token.replace(/~/g, '~0').replace(/\//g, '~1');
}

export function useChildren(
	children: ChildList | undefined,
	visited?: Set<string>,
): React.ReactNode {
	const { state } = useSurfaceContext();
	const { basePath } = useDataContext();
	const { dataModel } = state;
	const ComponentRenderer = ComponentRendererRef;

	if (!children || !ComponentRenderer) {
		return null;
	}

	// Static child list — array of component IDs
	if (Array.isArray(children) && !isChildTemplate(children)) {
		return (children as ChildReference[]).map((childId) => (
			<ComponentRenderer key={childId} componentId={childId} visited={visited} />
		));
	}

	// Template child list — { componentId, path }
	if (isChildTemplate(children)) {
		const resolvedPath = resolvePointerPath(basePath, children.path);
		const items = dataModel.get(resolvedPath);
		if (Array.isArray(items)) {
			return items.map((_item, index) => {
				const itemBasePath = `${resolvedPath}/${index}`;
				return (
					<DataContext.Provider key={index} value={{ basePath: itemBasePath }}>
						<ComponentRenderer componentId={children.componentId} visited={visited} />
					</DataContext.Provider>
				);
			});
		}

		if (items && typeof items === 'object') {
			const entries = Object.entries(items as Record<string, unknown>);
			return entries.map(([itemKey]) => {
				const encodedKey = encodeJsonPointerToken(itemKey);
				const itemBasePath = `${resolvedPath}/${encodedKey}`;
				return (
					<DataContext.Provider key={itemKey} value={{ basePath: itemBasePath }}>
						<ComponentRenderer componentId={children.componentId} visited={visited} />
					</DataContext.Provider>
				);
			});
		}

		const actualType = items === null ? 'null' : typeof items;
		throw new Error(
			`Template children binding at "${resolvedPath}" must resolve to an array or object map, received ${actualType}`,
		);
	}

	return null;
}

export function useChild(
	childId: ChildReference | undefined,
	visited?: Set<string>,
): React.ReactNode {
	const ComponentRenderer = ComponentRendererRef;

	if (!childId || !ComponentRenderer) {
		return null;
	}
	return <ComponentRenderer componentId={childId} visited={visited} />;
}
